import React, { useState, useEffect, useCallback } from 'react'
import { Box, Slide, IconButton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import AgoraRTC from 'agora-rtc-sdk-ng'
import {
  getAndSetMicrophones,
  getAndSetCameras,
  getAndSetSpeakers,
} from 'src/consumer/components/LiveSessionPage/LiveSessionPageComponents'

/**
 * MediaPermissionsSheet
 * 
 * A mobile-first, user-friendly permission request component designed for
 * users who may not be tech-savvy. Features:
 * 
 * 1. Clear, warm messaging appropriate for therapy/consultation sessions
 * 2. Full-screen bottom sheet on mobile for better UX
 * 3. Platform-specific recovery guidance (iOS/Android/Desktop)
 * 4. Step-by-step instructions if permissions are blocked
 * 5. Large touch targets for easy interaction
 * 6. Calm, reassuring visual design
 */

// Detect platform for permission recovery instructions
const getPlatformInfo = () => {
  const ua = navigator.userAgent || navigator.vendor || window.opera
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  const isAndroid = /android/i.test(ua)
  const isMobile = isIOS || isAndroid || window.innerWidth < 768
  const isSafari = /^((?!chrome|android).)*safari/i.test(ua)
  const isChrome = /chrome/i.test(ua) && !/edge/i.test(ua)
  
  return { isIOS, isAndroid, isMobile, isSafari, isChrome }
}

// Permission states
const PERMISSION_STATES = {
  INITIAL: 'initial',
  REQUESTING: 'requesting',
  GRANTED: 'granted',
  DENIED: 'denied',
  BLOCKED: 'blocked',
  TIMEOUT: 'timeout',      // Device took too long to respond
  NOT_FOUND: 'not_found',  // No camera/mic found
  IN_USE: 'in_use',        // Device in use by another app
}

export default function MediaPermissionsSheet({
  open,
  onClose,
  onPermissionsGranted,
  onSkipPermissions, // Called when user chooses to continue without camera/mic
  setMicrophoneError,
  setCameraError,
  setAvailableMicrophones,
  setSelectedMicrophone,
  setAvailableSpeakers,
  setSelectedSpeaker,
  setAvailableCameras,
  setSelectedCamera,
  sessionType = 'session', // 'consultation', 'therapy', 'session'
}) {
  const [permissionState, setPermissionState] = useState(PERMISSION_STATES.INITIAL)
  const [platformInfo] = useState(getPlatformInfo)
  
  // Check current permission status on open
  useEffect(() => {
    if (open) {
      checkPermissionStatus()
    }
  }, [open])
  
  const checkPermissionStatus = async () => {
    try {
      // Check if permissions API is available
      if (navigator.permissions) {
        const [micPermission, camPermission] = await Promise.all([
          navigator.permissions.query({ name: 'microphone' }).catch(() => ({ state: 'prompt' })),
          navigator.permissions.query({ name: 'camera' }).catch(() => ({ state: 'prompt' })),
        ])
        
        if (micPermission.state === 'granted' && camPermission.state === 'granted') {
          setPermissionState(PERMISSION_STATES.GRANTED)
          handlePermissionsGranted()
          return
        }
        
        if (micPermission.state === 'denied' || camPermission.state === 'denied') {
          setPermissionState(PERMISSION_STATES.BLOCKED)
          return
        }
      }
      setPermissionState(PERMISSION_STATES.INITIAL)
    } catch (error) {
      setPermissionState(PERMISSION_STATES.INITIAL)
    }
  }
  
  const handlePermissionsGranted = useCallback(async () => {
    try {
      setMicrophoneError(false)
      setCameraError(false)
      
      // Use Promise.allSettled to ensure we continue even if some device enumeration fails
      // This is critical on iOS Safari where device enumeration may fail or return empty arrays
      await Promise.allSettled([
        getAndSetMicrophones({ setAvailableMicrophones, setSelectedMicrophone }),
        getAndSetCameras({ setAvailableCameras, setSelectedCamera }),
        getAndSetSpeakers({ setAvailableSpeakers, setSelectedSpeaker }),
      ])
      
      onPermissionsGranted?.()
    } catch (error) {
      console.error('Error setting up devices:', error)
    } finally {
      // Always close the dialog, even if device enumeration fails
      // This prevents the "Waiting for your permission" stuck state on iOS
      onClose()
    }
  }, [setMicrophoneError, setCameraError, setAvailableMicrophones, setSelectedMicrophone, setAvailableCameras, setSelectedCamera, setAvailableSpeakers, setSelectedSpeaker, onPermissionsGranted, onClose])
  
  const requestPermissions = async () => {
    setPermissionState(PERMISSION_STATES.REQUESTING)
    
    try {
      // Add a timeout wrapper to handle slow device responses
      // Increased timeout for iOS Safari which can be slow to activate camera
      const timeoutDuration = platformInfo.isIOS ? 30000 : 15000
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('TIMEOUT')), timeoutDuration)
      })
      
      const mediaPromise = navigator.mediaDevices.getUserMedia({ 
        audio: true, 
        video: true 
      })
      
      const stream = await Promise.race([mediaPromise, timeoutPromise])
      
      // Stop the stream immediately - we just needed to request permission
      stream.getTracks().forEach(track => track.stop())
      
      // On iOS Safari, add a small delay before device enumeration
      // This gives the browser time to properly register the permissions
      if (platformInfo.isIOS) {
        await new Promise(resolve => setTimeout(resolve, 500))
      }
      
      setPermissionState(PERMISSION_STATES.GRANTED)
      handlePermissionsGranted()
    } catch (error) {
      console.error('Permission request failed:', error)
      
      // Handle different error types
      const errorName = error.name || ''
      const errorMessage = error.message || ''
      
      if (errorName === 'NotAllowedError' || errorName === 'PermissionDeniedError') {
        // User denied permission
        setPermissionState(PERMISSION_STATES.BLOCKED)
        setMicrophoneError(true)
        setCameraError(true)
      } else if (
        errorName === 'AbortError' || 
        errorMessage.includes('TIMEOUT') ||
        errorMessage.includes('Timeout') ||
        errorMessage.includes('abort')
      ) {
        // Device timeout - camera/mic might be in use or slow to respond
        setPermissionState(PERMISSION_STATES.TIMEOUT)
        setMicrophoneError(true)
        setCameraError(true)
      } else if (errorName === 'NotFoundError' || errorName === 'DevicesNotFoundError') {
        // No camera/mic found
        setPermissionState(PERMISSION_STATES.NOT_FOUND)
        setMicrophoneError(true)
        setCameraError(true)
      } else if (errorName === 'NotReadableError' || errorName === 'TrackStartError') {
        // Device in use by another app
        setPermissionState(PERMISSION_STATES.IN_USE)
        setMicrophoneError(true)
        setCameraError(true)
      } else {
        // Generic error - on iOS Safari, try to proceed anyway
        // as the error might be a false negative
        if (platformInfo.isIOS) {
          console.warn('iOS Safari permission error - attempting to proceed:', error)
          setPermissionState(PERMISSION_STATES.GRANTED)
          handlePermissionsGranted()
        } else {
          setPermissionState(PERMISSION_STATES.DENIED)
          setMicrophoneError(true)
          setCameraError(true)
        }
      }
    }
  }
  
  const handleContinueWithout = () => {
    setMicrophoneError(true)
    setCameraError(true)
    onClose()
    // Notify parent that user chose to skip permissions
    // This allows the parent to proceed with the call even without permissions
    onSkipPermissions?.()
  }
  
  const getSessionTypeText = () => {
    switch (sessionType) {
      case 'consultation':
        return 'consultation'
      case 'therapy':
        return 'therapy session'
      default:
        return 'session'
    }
  }
  
  // Get platform-specific instructions for unblocking permissions
  const getRecoveryInstructions = () => {
    const { isIOS, isAndroid, isSafari, isChrome } = platformInfo
    
    if (isIOS) {
      return {
        title: 'How to allow on iPhone/iPad',
        steps: [
          'Open the Settings app on your device',
          'Scroll down and tap on Safari (or your browser name)',
          'Tap on "Camera" and select "Allow"',
          'Tap on "Microphone" and select "Allow"',
          'Come back here and tap "Try Again"',
        ],
        icon: '⚙️',
      }
    }
    
    if (isAndroid) {
      return {
        title: 'How to allow on Android',
        steps: [
          'Tap the lock icon (🔒) in the address bar at the top',
          'Tap "Permissions" or "Site settings"',
          'Turn on Camera and Microphone',
          'Come back and tap "Try Again"',
        ],
        icon: '🔒',
      }
    }
    
    if (isChrome) {
      return {
        title: 'How to allow in Chrome',
        steps: [
          'Click the camera icon (📷) in the address bar',
          'Select "Allow" for both camera and microphone',
          'Click "Try Again" below',
        ],
        icon: '📷',
      }
    }
    
    if (isSafari) {
      return {
        title: 'How to allow in Safari',
        steps: [
          'Click "Safari" in the top menu',
          'Go to "Settings for This Website..."',
          'Set Camera and Microphone to "Allow"',
          'Click "Try Again" below',
        ],
        icon: '🔧',
      }
    }
    
    return {
      title: 'How to allow camera & microphone',
      steps: [
        'Look for a camera or lock icon in your browser\'s address bar',
        'Click it and allow camera and microphone access',
        'Then tap "Try Again" below',
      ],
      icon: '🔧',
    }
  }
  
  if (!open) return null
  
  return (
    <Slide direction="up" in={open} mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          top: platformInfo.isMobile ? 0 : 'auto',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: platformInfo.isMobile ? 'flex-end' : 'center',
          alignItems: 'center',
          background: platformInfo.isMobile 
            ? 'rgba(0, 0, 0, 0.6)' 
            : 'transparent',
          backdropFilter: platformInfo.isMobile ? 'blur(4px)' : 'none',
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget && permissionState !== PERMISSION_STATES.REQUESTING) {
            onClose()
          }
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: platformInfo.isMobile ? '100%' : '480px',
            maxHeight: platformInfo.isMobile ? '90vh' : 'auto',
            background: '#ffffff',
            borderRadius: platformInfo.isMobile ? '24px 24px 0 0' : '24px',
            boxShadow: '0 -4px 32px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Handle bar for mobile */}
          {platformInfo.isMobile && (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                pt: 1.5,
                pb: 0.5,
              }}
            >
              <Box
                sx={{
                  width: '40px',
                  height: '4px',
                  borderRadius: '2px',
                  background: '#d1d5db',
                }}
              />
            </Box>
          )}
          
          {/* Close button */}
          {permissionState !== PERMISSION_STATES.REQUESTING && (
            <IconButton
              onClick={onClose}
              sx={{
                position: 'absolute',
                top: platformInfo.isMobile ? '16px' : '12px',
                right: '12px',
                color: '#6b7280',
                zIndex: 1,
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
          
          {/* Content */}
          <Box
            sx={{
              p: { xs: 3, sm: 4 },
              pt: { xs: 2, sm: 3 },
              overflowY: 'auto',
              flex: 1,
            }}
          >
            {permissionState === PERMISSION_STATES.INITIAL && (
              <InitialPermissionContent 
                sessionType={getSessionTypeText()}
                onAllow={requestPermissions}
                onSkip={handleContinueWithout}
                isMobile={platformInfo.isMobile}
              />
            )}
            
            {permissionState === PERMISSION_STATES.REQUESTING && (
              <RequestingContent />
            )}
            
            {permissionState === PERMISSION_STATES.BLOCKED && (
              <BlockedPermissionContent
                instructions={getRecoveryInstructions()}
                onRetry={requestPermissions}
                onSkip={handleContinueWithout}
                isMobile={platformInfo.isMobile}
              />
            )}
            
            {permissionState === PERMISSION_STATES.DENIED && (
              <DeniedPermissionContent
                onRetry={requestPermissions}
                onSkip={handleContinueWithout}
                isMobile={platformInfo.isMobile}
              />
            )}
            
            {permissionState === PERMISSION_STATES.TIMEOUT && (
              <TimeoutErrorContent
                onRetry={requestPermissions}
                onSkip={handleContinueWithout}
                isMobile={platformInfo.isMobile}
              />
            )}
            
            {permissionState === PERMISSION_STATES.NOT_FOUND && (
              <DeviceNotFoundContent
                onRetry={requestPermissions}
                onSkip={handleContinueWithout}
                isMobile={platformInfo.isMobile}
              />
            )}
            
            {permissionState === PERMISSION_STATES.IN_USE && (
              <DeviceInUseContent
                onRetry={requestPermissions}
                onSkip={handleContinueWithout}
                isMobile={platformInfo.isMobile}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Slide>
  )
}

// Initial permission request content
function InitialPermissionContent({ sessionType, onAllow, onSkip, isMobile }) {
  return (
    <Box sx={{ textAlign: 'center' }}>
      {/* Warm, friendly icon */}
      <Box
        sx={{
          width: '100px',
          height: '100px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px',
          boxShadow: '0 4px 16px rgba(99, 102, 241, 0.2)',
        }}
      >
        {/* Video call illustration */}
        <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
          {/* Person silhouette */}
          <circle cx="28" cy="20" r="10" fill="#6366f1" opacity="0.9"/>
          <ellipse cx="28" cy="42" rx="16" ry="10" fill="#6366f1" opacity="0.9"/>
          {/* Mic icon */}
          <circle cx="44" cy="40" r="8" fill="#10b981"/>
          <rect x="42" y="36" width="4" height="6" rx="2" fill="white"/>
          <path d="M40 42 C40 44 42 46 44 46 C46 46 48 44 48 42" stroke="white" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
          <line x1="44" y1="46" x2="44" y2="48" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      </Box>
      
      {/* Main title */}
      <Box
        component="h2"
        sx={{
          fontSize: { xs: '22px', sm: '26px' },
          fontWeight: 700,
          color: '#111827',
          m: 0,
          mb: 1.5,
          lineHeight: 1.3,
        }}
      >
        Your doctor needs to see and hear you
      </Box>
      
      {/* Subtitle */}
      <Box
        component="p"
        sx={{
          fontSize: { xs: '15px', sm: '16px' },
          color: '#6b7280',
          m: 0,
          mb: 3,
          lineHeight: 1.6,
        }}
      >
        To have a proper {sessionType}, please allow camera and microphone access. 
        This helps your healthcare provider understand you better.
      </Box>
      
      {/* Trust indicators */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          mb: 4,
          textAlign: 'left',
          background: '#f9fafb',
          borderRadius: '12px',
          p: 2,
        }}
      >
        <TrustItem icon="🔒" text="Your video is private and secure" />
        <TrustItem icon="⏹️" text="You can turn off camera anytime during the call" />
        <TrustItem icon="🔇" text="You can mute yourself whenever you want" />
      </Box>
      
      {/* Primary action - Allow */}
      <Box
        component="button"
        onClick={onAllow}
        sx={{
          width: '100%',
          padding: { xs: '16px', sm: '18px' },
          fontSize: { xs: '17px', sm: '18px' },
          fontWeight: 600,
          color: '#ffffff',
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          border: 'none',
          borderRadius: '14px',
          cursor: 'pointer',
          boxShadow: '0 4px 14px rgba(16, 185, 129, 0.35)',
          transition: 'all 0.2s ease',
          mb: 2,
          touchAction: 'manipulation',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 6px 20px rgba(16, 185, 129, 0.4)',
          },
          '&:active': {
            transform: 'scale(0.98)',
          },
        }}
      >
        ✓ Allow Camera & Microphone
      </Box>
      
      {/* Secondary action - Skip (discouraged) */}
      <Box
        component="button"
        onClick={onSkip}
        sx={{
          width: '100%',
          padding: '12px',
          fontSize: '14px',
          fontWeight: 500,
          color: '#9ca3af',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          transition: 'color 0.2s',
          '&:hover': {
            color: '#6b7280',
          },
        }}
      >
        Join without camera/microphone
      </Box>
      
      <Box
        sx={{
          fontSize: '12px',
          color: '#d1d5db',
          mt: 1,
        }}
      >
        (Not recommended - your doctor won't be able to see or hear you)
      </Box>
    </Box>
  )
}

// Trust indicator item
function TrustItem({ icon, text }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
      }}
    >
      <span style={{ fontSize: '18px' }}>{icon}</span>
      <span style={{ fontSize: '14px', color: '#4b5563' }}>{text}</span>
    </Box>
  )
}

// Requesting permission (waiting for browser prompt)
function RequestingContent() {
  return (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      {/* Pulsing animation */}
      <Box
        sx={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px',
          animation: 'pulse 1.5s ease-in-out infinite',
          '@keyframes pulse': {
            '0%, 100%': { transform: 'scale(1)', opacity: 1 },
            '50%': { transform: 'scale(1.05)', opacity: 0.8 },
          },
        }}
      >
        <span style={{ fontSize: '36px' }}>👆</span>
      </Box>
      
      <Box
        component="h3"
        sx={{
          fontSize: '20px',
          fontWeight: 600,
          color: '#111827',
          m: 0,
          mb: 1.5,
        }}
      >
        Tap "Allow" when prompted
      </Box>
      
      <Box
        sx={{
          fontSize: '15px',
          color: '#6b7280',
          lineHeight: 1.6,
        }}
      >
        A popup will appear at the top of your screen.
        <br />
        Please tap <strong>"Allow"</strong> to continue.
      </Box>
      
      {/* Visual hint */}
      <Box
        sx={{
          mt: 4,
          p: 2,
          background: '#f3f4f6',
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
        }}
      >
        <Box
          sx={{
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            background: '#fbbf24',
            animation: 'blink 1s ease-in-out infinite',
            '@keyframes blink': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.4 },
            },
          }}
        />
        <span style={{ fontSize: '14px', color: '#6b7280' }}>
          Waiting for your permission...
        </span>
      </Box>
    </Box>
  )
}

// Permission blocked - show recovery instructions
function BlockedPermissionContent({ instructions, onRetry, onSkip, isMobile }) {
  return (
    <Box>
      {/* Warning icon */}
      <Box
        sx={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 20px',
        }}
      >
        <span style={{ fontSize: '36px' }}>{instructions.icon}</span>
      </Box>
      
      <Box
        component="h2"
        sx={{
          fontSize: { xs: '20px', sm: '22px' },
          fontWeight: 700,
          color: '#111827',
          m: 0,
          mb: 1,
          textAlign: 'center',
        }}
      >
        Camera & Microphone Blocked
      </Box>
      
      <Box
        sx={{
          fontSize: '14px',
          color: '#ef4444',
          textAlign: 'center',
          mb: 3,
          fontWeight: 500,
        }}
      >
        Your doctor won't be able to see or hear you
      </Box>
      
      {/* Recovery instructions */}
      <Box
        sx={{
          background: '#f9fafb',
          borderRadius: '16px',
          p: 2.5,
          mb: 3,
        }}
      >
        <Box
          sx={{
            fontSize: '16px',
            fontWeight: 600,
            color: '#111827',
            mb: 2,
          }}
        >
          {instructions.title}
        </Box>
        
        <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
          {instructions.steps.map((step, index) => (
            <Box
              component="li"
              key={index}
              sx={{
                fontSize: '14px',
                color: '#4b5563',
                mb: 1.5,
                lineHeight: 1.5,
                '&::marker': {
                  color: '#6366f1',
                  fontWeight: 600,
                },
              }}
            >
              {step}
            </Box>
          ))}
        </Box>
      </Box>
      
      {/* Retry button */}
      <Box
        component="button"
        onClick={onRetry}
        sx={{
          width: '100%',
          padding: { xs: '16px', sm: '18px' },
          fontSize: { xs: '16px', sm: '17px' },
          fontWeight: 600,
          color: '#ffffff',
          background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
          border: 'none',
          borderRadius: '14px',
          cursor: 'pointer',
          boxShadow: '0 4px 14px rgba(99, 102, 241, 0.35)',
          transition: 'all 0.2s ease',
          mb: 2,
          touchAction: 'manipulation',
          '&:hover': {
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'scale(0.98)',
          },
        }}
      >
        🔄 I've allowed it - Try Again
      </Box>
      
      {/* Skip option */}
      <Box
        component="button"
        onClick={onSkip}
        sx={{
          width: '100%',
          padding: '12px',
          fontSize: '14px',
          fontWeight: 500,
          color: '#9ca3af',
          background: 'transparent',
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          cursor: 'pointer',
        }}
      >
        Continue without camera/microphone
      </Box>
    </Box>
  )
}

// Permission denied (but not blocked in settings)
function DeniedPermissionContent({ onRetry, onSkip, isMobile }) {
  return (
    <Box sx={{ textAlign: 'center' }}>
      <Box
        sx={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 20px',
        }}
      >
        <span style={{ fontSize: '36px' }}>😕</span>
      </Box>
      
      <Box
        component="h2"
        sx={{
          fontSize: { xs: '20px', sm: '22px' },
          fontWeight: 700,
          color: '#111827',
          m: 0,
          mb: 1.5,
        }}
      >
        Oops! Permission was denied
      </Box>
      
      <Box
        sx={{
          fontSize: '15px',
          color: '#6b7280',
          mb: 4,
          lineHeight: 1.6,
        }}
      >
        Don't worry! You can try again.
        <br />
        This time, please tap <strong>"Allow"</strong> when asked.
      </Box>
      
      {/* Visual hint */}
      <Box
        sx={{
          background: '#ecfdf5',
          border: '1px solid #a7f3d0',
          borderRadius: '12px',
          p: 2,
          mb: 4,
          textAlign: 'left',
        }}
      >
        <Box
          sx={{
            fontSize: '14px',
            color: '#065f46',
            fontWeight: 500,
            mb: 1,
          }}
        >
          💡 Tip: When the popup appears
        </Box>
        <Box sx={{ fontSize: '14px', color: '#047857' }}>
          Look for a popup asking for camera and microphone access.
          Tap the <strong>green "Allow" button</strong>.
        </Box>
      </Box>
      
      {/* Retry button */}
      <Box
        component="button"
        onClick={onRetry}
        sx={{
          width: '100%',
          padding: { xs: '16px', sm: '18px' },
          fontSize: { xs: '16px', sm: '17px' },
          fontWeight: 600,
          color: '#ffffff',
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          border: 'none',
          borderRadius: '14px',
          cursor: 'pointer',
          boxShadow: '0 4px 14px rgba(16, 185, 129, 0.35)',
          transition: 'all 0.2s ease',
          mb: 2,
          touchAction: 'manipulation',
          '&:active': {
            transform: 'scale(0.98)',
          },
        }}
      >
        ✓ Try Again - I'll allow this time
      </Box>
      
      {/* Skip option */}
      <Box
        component="button"
        onClick={onSkip}
        sx={{
          width: '100%',
          padding: '12px',
          fontSize: '14px',
          fontWeight: 500,
          color: '#9ca3af',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
        }}
      >
        Join without camera/microphone
      </Box>
    </Box>
  )
}

// Timeout error - device took too long to respond
function TimeoutErrorContent({ onRetry, onSkip, isMobile }) {
  return (
    <Box sx={{ textAlign: 'center' }}>
      <Box
        sx={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 20px',
        }}
      >
        <span style={{ fontSize: '36px' }}>⏱️</span>
      </Box>
      
      <Box
        component="h2"
        sx={{
          fontSize: { xs: '20px', sm: '22px' },
          fontWeight: 700,
          color: '#111827',
          m: 0,
          mb: 1.5,
        }}
      >
        Camera took too long to start
      </Box>
      
      <Box
        sx={{
          fontSize: '15px',
          color: '#6b7280',
          mb: 3,
          lineHeight: 1.6,
        }}
      >
        Your camera or microphone is taking longer than expected to respond.
      </Box>
      
      {/* Troubleshooting tips */}
      <Box
        sx={{
          background: '#fefce8',
          border: '1px solid #fde047',
          borderRadius: '12px',
          p: 2,
          mb: 4,
          textAlign: 'left',
        }}
      >
        <Box
          sx={{
            fontSize: '14px',
            color: '#854d0e',
            fontWeight: 600,
            mb: 1.5,
          }}
        >
          Try these steps:
        </Box>
        <Box component="ul" sx={{ m: 0, pl: 2, color: '#a16207', fontSize: '14px' }}>
          <li style={{ marginBottom: '8px' }}>Close other apps using your camera (Zoom, Teams, etc.)</li>
          <li style={{ marginBottom: '8px' }}>Wait a few seconds and try again</li>
          <li style={{ marginBottom: '8px' }}>Restart your browser if it keeps happening</li>
        </Box>
      </Box>
      
      {/* Retry button */}
      <Box
        component="button"
        onClick={onRetry}
        sx={{
          width: '100%',
          padding: { xs: '16px', sm: '18px' },
          fontSize: { xs: '16px', sm: '17px' },
          fontWeight: 600,
          color: '#ffffff',
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          border: 'none',
          borderRadius: '14px',
          cursor: 'pointer',
          boxShadow: '0 4px 14px rgba(245, 158, 11, 0.35)',
          transition: 'all 0.2s ease',
          mb: 2,
          touchAction: 'manipulation',
          '&:active': {
            transform: 'scale(0.98)',
          },
        }}
      >
        🔄 Try Again
      </Box>
      
      {/* Skip option */}
      <Box
        component="button"
        onClick={onSkip}
        sx={{
          width: '100%',
          padding: '12px',
          fontSize: '14px',
          fontWeight: 500,
          color: '#9ca3af',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
        }}
      >
        Join without camera/microphone
      </Box>
    </Box>
  )
}

// Device not found - no camera/mic detected
function DeviceNotFoundContent({ onRetry, onSkip, isMobile }) {
  return (
    <Box sx={{ textAlign: 'center' }}>
      <Box
        sx={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 20px',
        }}
      >
        <span style={{ fontSize: '36px' }}>🔍</span>
      </Box>
      
      <Box
        component="h2"
        sx={{
          fontSize: { xs: '20px', sm: '22px' },
          fontWeight: 700,
          color: '#111827',
          m: 0,
          mb: 1.5,
        }}
      >
        Camera or microphone not found
      </Box>
      
      <Box
        sx={{
          fontSize: '15px',
          color: '#6b7280',
          mb: 3,
          lineHeight: 1.6,
        }}
      >
        We couldn't detect a camera or microphone on your device.
      </Box>
      
      {/* Tips */}
      <Box
        sx={{
          background: '#f3e8ff',
          border: '1px solid #d8b4fe',
          borderRadius: '12px',
          p: 2,
          mb: 4,
          textAlign: 'left',
        }}
      >
        <Box
          sx={{
            fontSize: '14px',
            color: '#6b21a8',
            fontWeight: 600,
            mb: 1.5,
          }}
        >
          Check the following:
        </Box>
        <Box component="ul" sx={{ m: 0, pl: 2, color: '#7c3aed', fontSize: '14px' }}>
          <li style={{ marginBottom: '8px' }}>Is your camera/microphone plugged in properly?</li>
          <li style={{ marginBottom: '8px' }}>Try using a different USB port</li>
          <li style={{ marginBottom: '8px' }}>Check if your device has a built-in camera</li>
        </Box>
      </Box>
      
      {/* Retry button */}
      <Box
        component="button"
        onClick={onRetry}
        sx={{
          width: '100%',
          padding: { xs: '16px', sm: '18px' },
          fontSize: { xs: '16px', sm: '17px' },
          fontWeight: 600,
          color: '#ffffff',
          background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
          border: 'none',
          borderRadius: '14px',
          cursor: 'pointer',
          boxShadow: '0 4px 14px rgba(139, 92, 246, 0.35)',
          transition: 'all 0.2s ease',
          mb: 2,
          touchAction: 'manipulation',
          '&:active': {
            transform: 'scale(0.98)',
          },
        }}
      >
        🔄 Check Again
      </Box>
      
      {/* Skip option */}
      <Box
        component="button"
        onClick={onSkip}
        sx={{
          width: '100%',
          padding: '12px',
          fontSize: '14px',
          fontWeight: 500,
          color: '#9ca3af',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
        }}
      >
        Join without camera/microphone
      </Box>
    </Box>
  )
}

// Device in use by another app
function DeviceInUseContent({ onRetry, onSkip, isMobile }) {
  return (
    <Box sx={{ textAlign: 'center' }}>
      <Box
        sx={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 20px',
        }}
      >
        <span style={{ fontSize: '36px' }}>📵</span>
      </Box>
      
      <Box
        component="h2"
        sx={{
          fontSize: { xs: '20px', sm: '22px' },
          fontWeight: 700,
          color: '#111827',
          m: 0,
          mb: 1.5,
        }}
      >
        Camera is being used
      </Box>
      
      <Box
        sx={{
          fontSize: '15px',
          color: '#6b7280',
          mb: 3,
          lineHeight: 1.6,
        }}
      >
        Your camera or microphone is currently being used by another app.
      </Box>
      
      {/* Instructions */}
      <Box
        sx={{
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '12px',
          p: 2,
          mb: 4,
          textAlign: 'left',
        }}
      >
        <Box
          sx={{
            fontSize: '14px',
            color: '#991b1b',
            fontWeight: 600,
            mb: 1.5,
          }}
        >
          Please close these apps:
        </Box>
        <Box component="ul" sx={{ m: 0, pl: 2, color: '#b91c1c', fontSize: '14px' }}>
          <li style={{ marginBottom: '8px' }}>Zoom, Microsoft Teams, or Google Meet</li>
          <li style={{ marginBottom: '8px' }}>FaceTime or other video call apps</li>
          <li style={{ marginBottom: '8px' }}>Any app with a video/photo feature open</li>
        </Box>
      </Box>
      
      {/* Retry button */}
      <Box
        component="button"
        onClick={onRetry}
        sx={{
          width: '100%',
          padding: { xs: '16px', sm: '18px' },
          fontSize: { xs: '16px', sm: '17px' },
          fontWeight: 600,
          color: '#ffffff',
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          border: 'none',
          borderRadius: '14px',
          cursor: 'pointer',
          boxShadow: '0 4px 14px rgba(239, 68, 68, 0.35)',
          transition: 'all 0.2s ease',
          mb: 2,
          touchAction: 'manipulation',
          '&:active': {
            transform: 'scale(0.98)',
          },
        }}
      >
        🔄 I've closed other apps - Try Again
      </Box>
      
      {/* Skip option */}
      <Box
        component="button"
        onClick={onSkip}
        sx={{
          width: '100%',
          padding: '12px',
          fontSize: '14px',
          fontWeight: 500,
          color: '#9ca3af',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
        }}
      >
        Join without camera/microphone
      </Box>
    </Box>
  )
}

